import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AppHttpModule } from '@app/app-http.module';
import { AppTranslateModule } from '@app/app-translate.module';
import { AddCatalogItemsDialogComponent } from '@components/add-catalog-items-dialog/1.0/add-catalog-items-dialog.component';
import { AssociateItemsDialogComponent } from '@components/associate-items-dialog/1.0/associate-items-dialog.component';
import { PriceLevelsDialogComponent } from '@components/price-levels-dialog/1.0/price-levels-dialog.component';
import { AddUsersDialogComponent } from '@components/add-users-dialog/1.0/add-users-dialog.component';
import { BadgeComponent } from '@components/badge/1.0';
import { ButtonComponent } from '@components/button/1.0';
import { ChangeSetNoticeComponent } from '@components/change-set-notice/change-set-notice.component';
import { ChangeSetScheduleDialogComponent } from '@components/change-set-schedule-dialog/1.0';
import { ChangeSetConvertDialogComponent } from '@components/change-set-convert-dialog/1.0';
import { ChangeSetChangesDialogComponent } from '@components/change-set-changes-dialog/1.0';
import { ConfirmationComponent } from '@components/confirmation/1.0/confirmation.component';
import { DateRangeCvaComponent } from '@components/date-range-cva/date-range-cva.component';
import { FileUploadComponent } from '@components/file-upload/1.0/file-upload.component';
import { ItemDecoratorCvaComponent } from '@components/item-decorator/item-decorator-cva.component';
import { ItemViewComponent } from '@components/item-view/1.0/item-view.component';
import { ArrayTypeComponent } from '@components/json-form-cva/1.0/array-type.component';
import { JsonFormCvaComponent } from '@components/json-form-cva/1.0/json-form-cva.component';
import { ObjectTypeComponent } from '@components/json-form-cva/1.0/object-type.component';
import { LandingComponent } from '@components/landing/1.0';
import { LoadingComponent } from '@components/loading/1.0';
import { LogoComponent } from '@components/logo/1.0';
import { MenuItemComponent } from '@components/menu-item/1.0';
import { MessageComponent } from '@components/message/1.0';
import { SelectCheckAllComponent } from '@components/select-check-all';
import { SelectMultiItemsDialogComponent } from '@components/select-multi-items-dialog/1.0';
import { CopyCatalogItemDialogComponent } from '@components/copy-catalog-item-dialog/1.0/copy-catalog-item-dialog.component';
import { SignInComponent } from '@components/sign-in/1.0';
import { SpecialHoursTemplateFormComponent } from '@components/special-hours-template-form/1.0/special-hours-template-form.component';
import { StoreConfigStatusComponent } from '@components/store-config-status/1.0/store-config-status/store-config-status.component';
import { StoreHoursTemplateFormComponent } from '@components/store-hours-template-form/1.0/store-hours-template-form.component';
import { SubtitleComponent } from '@components/subtitle/1.0';
import { TitleComponent } from '@components/title/1.0';
import { TypeSelectorComponent } from '@components/type-selector/1.0/type-selector.component';
import { VerticalNavComponent } from '@components/vertical-nav/vertical-nav.component';
import { WeatherComponent } from '@components/weather/1.0/weather.component';
import { AltNavComponent } from '@compounds/alt-nav/1.0';
import { AppHeaderComponent } from '@compounds/app-header/1.0';
import { ContentComponent } from '@compounds/content/1.0';
import { HeaderComponent } from '@compounds/header/1.0';
import { LanguageToggleComponent } from '@compounds/language-toggle/1.0';
import { MenuComponent } from '@compounds/menu/1.0';
import { ConfigShowDirective } from '@directives/config-show/1.0/config-show.directive';
import { FormSubmittedSyncDirective } from '@directives/form-submitted-sync/1.0';
import { NumbersOnlyDirective } from '@directives/numbers-only/1.0/numbers-only.directive';
import { CoreTableFixedVirtualScrollDirective } from '@directives/virtual-scroll/1.0/virtual-scroll.directive';
import { FormlyModule, FORMLY_CONFIG } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@services/analytics/1.0/analytics.service';
import { AppConfigService } from '@services/app-config/1.0/app-config.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ItemListComponent } from '@components/item-list/1.0/item-list.component';
import { AngularMatModule } from './app-materials.module';
import { BrandDetailComponent } from '@app/brand/brand-dashboard/brand-detail/brand-detail.component';
import { AggregatorNamePipe, CapitalizePipe } from './pipes/index';
import { StoreStatusPipe } from './store/pipes/index';
import { SelectProductsDialogComponent } from '@components/select-products-dialog/1.0/select-products-dialog.component';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { CatalogItemImagesComponent } from '@components/catalog-item-images/catalog-item-images.component';
import { TxpIntegersOnlyDirective } from '@directives/integers-only/1.0/txp-integers-only.directive';
import { DynamicOrderIntervalsTemplateFormComponent } from '@components/dynamic-order-intervals-template-form/1.0/dynamic-order-intervals-template-form';
import { DynamicPrepTimesTemplateFormComponent } from '@components/dynamic-prep-times-template-form/1.0/dynamic-prep-times-template-form.component';
import { ItemThresholdTemplateFormComponent } from '@components/item-thresholds-template-form/1.0/item-threshold-template-form.component';
import { AdvancedNoticeTemplateFormComponent } from '@components/advanced-notice-template-form/1.0/advanced-notice-template-form.component';
import { ItemPrepGroupDialogComponent } from '@components/item-prep-group-dialog/1.0/item-prep-group-dialog.component';
import { ItemPrepGroupsTemplateFormComponent } from '@components/item-prep-groups-template-form/1.0/item-prep-groups-template-form.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxMatColorPickerModule } from '@nng-components/angular-material-color-picker';
import { OrderLimitsTemplateFormComponent } from '@components/order-limits-template-form/order-limits-template-form.component';
import { DeleteKeyDialogComponent } from '@components/delete-key-dialog/delete-key-dialog.component';
import { TenderTemplateComponent } from '@components/tender-template/tender-template.component';
import { StoreOrderingDeactivateDialogComponent } from '@components/store-ordering-deactivate-dialog/1.0/store-ordering-deactivate-dialog';
import { OrderDetailsDialogComponent } from '@components/order-details-dialog/order-details-dialog.component';
import { CancelOrderDialogComponent } from '@components/cancel-order-dialog/cancel-order-dialog.component';
import { PosChannelTypesComponent } from '@components/pos-channel-types/pos-channel-types.component';
import { ItemsCvaComponent } from '@components/items-cva/items-cva.component';
import { CouponItemsDialogComponent } from '@components/items-cva/coupon-items-dialog/coupon-items-dialog.component';
import { DistributionRulesComponent } from '@components/distribution-rules/distribution-rules.component';
import { EditRuleComponent } from '@components/edit-rule/edit-rule.component';
import { CatalogItemImagesBaseComponent } from '@components/catalog-item-images-base/catalog-item-images-base.component';
import { CropImageDialogComponent } from '@components/crop-image-dialog/1.0/crop-image-dialog.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { EnterKeyDownDirective } from '@directives/enter-key-down/1.0/enter-key-down.directive';
import { RichTextEditorComponent } from '@components/rich-text-editor/1.0/rich-text-editor.component';
import { SegmentsGridComponent } from '@components/segments-grid/segments-grid.component';
import { EditSegmentComponent } from '@components/edit-segment/edit-segment.component';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { DistributionJobDialogComponent } from '@components/distributon-job-dialog/distribution-job-dialog.component';
import { LoadingDialogComponent } from '@components/loading-dialog/loading-dialog.component';
import { OrderPdfComponent } from '@app/orders/orders-dashboard/order-pdf/order-pdf.component';
import { SearchCatalogComponent } from '@components/search-catalog-dialog/1.0/search-catalog-dialog.component';
import { SelectBulkStoresDialogComponent } from '@components/select-bulk-stores/1.0/select-bulk-stores-dialog.component';
import { CatalogXenialDetailsComponent } from '@components/catalog-xenial-details/1.0/catalog-xenial-details.component';
import { CatalogXenialStoresComponent } from '@components/catalog-xenial-stores/1.0/catalog-xenial-stores.component';
import { CreateDayPartsDialogComponent } from '@components/dayparts/create-daypart-dialog/create-daypart-dialog.component';
import { DaypartsListComponent } from '@components/dayparts/dayparts-list/dayparts-list.component';
import { CatalogItemTagsComponent } from '@app/catalog/catalog-item-tags/catalog-item-tags.component';
import { StoreGroupSelectComponent } from '@components/store-group-select/1.0/store-group-select.component';

const COMPONENTS = [
  AdvancedNoticeTemplateFormComponent,
  ArrayTypeComponent,
  BadgeComponent,
  BrandDetailComponent,
  ButtonComponent,
  ChangeSetNoticeComponent,
  CatalogXenialStoresComponent,
  CatalogXenialDetailsComponent,
  DynamicOrderIntervalsTemplateFormComponent,
  DynamicPrepTimesTemplateFormComponent,
  FileUploadComponent,
  ItemDecoratorCvaComponent,
  ItemListComponent,
  ItemPrepGroupDialogComponent,
  ItemPrepGroupsTemplateFormComponent,
  ItemThresholdTemplateFormComponent,
  ItemViewComponent,
  JsonFormCvaComponent,
  LandingComponent,
  LoadingComponent,
  LogoComponent,
  MenuItemComponent,
  MessageComponent,
  ObjectTypeComponent,
  OrderLimitsTemplateFormComponent,
  SignInComponent,
  SpecialHoursTemplateFormComponent,
  StoreConfigStatusComponent,
  StoreHoursTemplateFormComponent,
  SubtitleComponent,
  TitleComponent,
  TypeSelectorComponent,
  VerticalNavComponent,
  WeatherComponent,
  ItemDecoratorCvaComponent,
  DateRangeCvaComponent,
  SelectCheckAllComponent,
  CatalogItemImagesComponent,
  CatalogItemImagesBaseComponent,
  DeleteKeyDialogComponent,
  TenderTemplateComponent,
  PosChannelTypesComponent,
  ItemsCvaComponent,
  CouponItemsDialogComponent,
  DistributionRulesComponent,
  EditRuleComponent,
  DistributionJobDialogComponent,
  CropImageDialogComponent,
  RichTextEditorComponent,
  SegmentsGridComponent,
  EditSegmentComponent,
  LoadingDialogComponent,
  OrderPdfComponent,
  SearchCatalogComponent,
  CreateDayPartsDialogComponent,
  DaypartsListComponent,
  CatalogItemTagsComponent,
  StoreGroupSelectComponent,
];

const COMPOUNDS = [
  AltNavComponent,
  AppHeaderComponent,
  ContentComponent,
  HeaderComponent,
  LanguageToggleComponent,
  MenuComponent,
];

const DIALOGS = [
  AddCatalogItemsDialogComponent,
  AddUsersDialogComponent,
  AssociateItemsDialogComponent,
  ChangeSetChangesDialogComponent,
  ChangeSetConvertDialogComponent,
  ChangeSetScheduleDialogComponent,
  ConfirmationComponent,
  CopyCatalogItemDialogComponent,
  OrderDetailsDialogComponent,
  PriceLevelsDialogComponent,
  StoreOrderingDeactivateDialogComponent,
  SelectMultiItemsDialogComponent,
  SelectProductsDialogComponent,
  CancelOrderDialogComponent,
  SelectBulkStoresDialogComponent,
];

const DIRECTIVES = [
  ConfigShowDirective,
  CoreTableFixedVirtualScrollDirective,
  FormSubmittedSyncDirective,
  NumbersOnlyDirective,
  TxpIntegersOnlyDirective,
  EnterKeyDownDirective,
];

const SERVICES = [
  AppConfigService,
  {
    provide: APP_INITIALIZER,
    useFactory: initializeApp,
    deps: [AppConfigService],
    multi: true,
  },
  AnalyticsService,
];

const APP_MODULES = [
  AppHttpModule,
  AngularMatModule,
  AppTranslateModule,
  RouterModule,
  NgxMatColorPickerModule,
  NgxMatInputTelComponent,
  ImageCropperModule,
  InfiniteScrollModule,
  NgClickOutsideDirective,
];

const APP_PIPES = [StoreStatusPipe, AggregatorNamePipe, CapitalizePipe];

export function formlyValidationConfig(translate: TranslateService) {
  return {
    validationMessages: [
      {
        name: 'required',
        message: () => {
          return translate.stream('form.error.required');
        },
      },
      {
        name: 'posNamespaceWithoutParent',
        message: () => {
          return translate.stream('store.posConfig.posNamespaceWithoutParentError');
        },
      },
    ],
  };
}

export function initializeApp(appConfig: AppConfigService) {
  return () => appConfig.load();
}

@NgModule({
  imports: [
    APP_MODULES,
    CommonModule,
    FormlyModule.forRoot({
      types: [
        { name: 'string', extends: 'input' },
        { name: 'boolean', extends: 'checkbox' },
        { name: 'array', component: ArrayTypeComponent },
        { name: 'object', component: ObjectTypeComponent },
        { name: 'enum', extends: 'select' },
        {
          name: 'number',
          extends: 'input',
          defaultOptions: { templateOptions: { type: 'number' } },
        },
        {
          name: 'integer',
          extends: 'input',
          defaultOptions: { templateOptions: { type: 'number' } },
        },
        {
          name: 'password',
          extends: 'input',
          defaultOptions: { templateOptions: { type: 'password' } },
        },
      ],
    }),
    FormlyMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
    TranslateModule,
    DragDropModule,
    NgxPermissionsModule,
    COMPONENTS,
    COMPOUNDS,
    DIALOGS,
    DIRECTIVES,
    APP_PIPES,
  ],
  exports: [
    APP_MODULES,
    COMPONENTS,
    COMPOUNDS,
    DIRECTIVES,
    FormlyMaterialModule,
    ReactiveFormsModule,
    APP_PIPES,
    ScrollingModule,
    NgxPermissionsModule,
    TranslateModule,
    AddCatalogItemsDialogComponent,
    AssociateItemsDialogComponent,
    PriceLevelsDialogComponent,
  ],
  providers: [
    SERVICES,
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: formlyValidationConfig,
      deps: [TranslateService],
    },
  ],
})
export class SharedModule {}
